// WHY (#1437): controlled decimal inputs in the China draft editor were
//   bound directly to a parsed number, so an in-progress fractional value
//   (`0`, `0,`, `.5`) collapsed after every keystroke and a value in (0,1)
//   like `0,543` could not be typed left-to-right. This hook owns a local
//   text buffer that is authoritative WHILE the field is focused and is
//   re-seeded from the numeric prop ONLY while the field is idle.

import { useRef, useState } from 'react';
import { formatDraftDecimal, parseDecimalInput } from '../draft-media/decimal.js';

export interface DecimalTextInput {
  text: string;
  onChange: (raw: string) => void;
  onFocus: () => void;
  onBlur: () => void;
}

// 4dp half-up; the `Number.EPSILON` nudge avoids float artifacts
// (e.g. 0.99985 -> 0.9999) and matches the round4 model used by the
// package/box totals (`Math.round(n * 10000) / 10000`).
function roundHalfUp4(n: number): number {
  return Math.round((n + Number.EPSILON) * 10000) / 10000;
}

/**
 * @aiPurpose Local text buffer for a controlled decimal field so
 *   in-progress fractional input is never wiped by a parent rerender.
 * @aiInvariant The buffer is the source of truth while the field is
 *   focused; an IDLE field re-seeds from `value` via render-phase state
 *   adjustment (NOT a focus-keyed effect — an effect re-seed races the
 *   parent rerender and re-introduces the keystroke collapse).
 */
export function useDecimalTextInput(
  value: number | null,
  emit: (next: number) => void,
): DecimalTextInput {
  const [text, setText] = useState(() => formatDraftDecimal(value));
  const [syncedValue, setSyncedValue] = useState<number | null>(value);
  const focusedRef = useRef(false);

  // Re-seed an IDLE field to the store value (FR-6). Render-phase state
  // adjustment, focus-guarded: never runs while the user is editing, so
  // in-progress decimals ("0,", "1.", ".5") are never wiped.
  if (!focusedRef.current && value !== syncedValue) {
    setSyncedValue(value);
    setText(formatDraftDecimal(value));
  }

  return {
    text,
    onChange: (raw) => {
      focusedRef.current = true;
      setText(raw);
      const parsed = parseDecimalInput(raw); // comma/dot handled here
      setSyncedValue(parsed); // our own emit must not trigger the reseed above
      emit(parsed);
    },
    onFocus: () => {
      focusedRef.current = true;
    },
    onBlur: () => {
      focusedRef.current = false;
      const rounded = roundHalfUp4(parseDecimalInput(text));
      setSyncedValue(rounded);
      setText(formatDraftDecimal(rounded)); // canonical dot form; 0/null -> ''
      emit(rounded); // store/payload match what the field shows
    },
  };
}
